import type {FileKey} from 'metaserver/static/js/encrypted_folder/ekms/types';
import type {
  Blockwise,
  PerRevisionKeyInfo,
} from 'metaserver/static/js/encrypted_folder/content/types';
import {
  calculateHmacSha256,
  createSymmetricCoreCrypto,
  verifyHmacSha256,
} from 'metaserver/static/js/encrypted_folder/crypto/core_crypto';
import {decryptPerRevisionKey} from 'metaserver/static/js/encrypted_folder/content/per_revision_key';
import type {Algorithm} from 'metaserver/static/js/encrypted_folder/crypto/types';

export const validateAuthSetHmac = async (
  fileKey: FileKey,
  algorithm: Algorithm,
  perRevisionKeyInfo: PerRevisionKeyInfo,
  blockwise: Blockwise
) => {
  const symmetricCrypto = createSymmetricCoreCrypto(fileKey.algorithm);
  const perRevisionKey = await decryptPerRevisionKey(perRevisionKeyInfo, algorithm, fileKey);
  const hmacKey = await symmetricCrypto.decrypt(
    blockwise.encryptedHmacKey,
    perRevisionKey,
    blockwise.hmacKeyEncryptionExtras
  );
  const authTags = blockwise.blockEncryptionExtras.map((extras) => extras.authTag);
  const authSetHmacMatches = await authSetHmacMatchesSignature(
    hmacKey,
    authTags,
    blockwise.setAuthTag
  );

  if (!authSetHmacMatches) {
    throw new Error('Invalid auth tags');
  }
};

const calculateAuthSetData = (authTags: Uint8Array[]) => {
  const authSetLength = authTags.map((authTag) => authTag.length).reduce((a, b) => a + b, 0);
  const authSet = new Uint8Array(authSetLength);
  let index = 0;
  authTags.forEach((authTag, _) => {
    authSet.set(authTag, index);
    index += authTag.length;
  });
  return authSet;
};

export const calculateAuthSetHmac = async (
  hmacKey: Uint8Array<ArrayBuffer>,
  authTags: Uint8Array[]
) => {
  const authSet = calculateAuthSetData(authTags);
  return calculateHmacSha256(hmacKey, authSet);
};

export const authSetHmacMatchesSignature = async (
  hmacKey: Uint8Array<ArrayBuffer>,
  authTags: Uint8Array[],
  signature: Uint8Array<ArrayBuffer>
) => {
  const authSet = calculateAuthSetData(authTags);
  return await verifyHmacSha256(hmacKey, authSet, signature);
};
